import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Banner, Button } from '@meshble/molo';
import { api, must, ApiError } from '../lib/api';
import { Anello, Brandmark, Logo } from '../brand';
import { Login, isUnauthenticated, useMe } from '../session';
import { t } from '../lib/lingua';

// Il token dell'invito arriva nel fragment (#token=...): il browser non lo
// manda al server, così non entra nei log di Caddy. Lo si mette subito in
// sessionStorage perché sopravviva al giro OIDC (che riporta a /invito senza
// fragment), e si tiene la query come ripiego per i link già spediti prima.
function risolviTokenInvito(daQuery: string): string {
  try {
    const daHash = new URLSearchParams(window.location.hash.slice(1)).get('token');
    if (daHash) {
      sessionStorage.setItem('invito', daHash);
      return daHash;
    }
    const salvato = sessionStorage.getItem('invito');
    if (salvato) return salvato;
  } catch {
    // sessionStorage non disponibile (finestra privata): resta la query.
  }
  return daQuery;
}

/** Il ruolo come lo legge chi è invitato; uno che qui non c'è, com'è. */
function ruolo(r: string): string {
  const nomi: Record<string, string> = {
    owner: t('proprietario'),
    admin: t('amministratore'),
    member: t('membro'),
    viewer: t('in sola lettura'),
    restricted: t('ristretto'),
  };
  return nomi[r] ?? r;
}

/**
 * Accettazione di un invito.
 *
 * È l'unica schermata che deve funzionare per chi **non** è ancora in nessuna
 * organizzazione, e per questo sta fuori dallo shell: lì `Shell` mostrerebbe
 * «nessuna organizzazione» prima che l'invito possa essere nemmeno letto.
 *
 * Entrare resta obbligatorio: l'anteprima dice cosa si sta per accettare senza
 * essere membri, ma accettare lega l'invito a un utente, e quell'utente deve
 * esistere.
 */
export function InvitoPage() {
  const { token: daQuery } = useSearch({ from: '/invito' });
  const [token] = useState(() => risolviTokenInvito(daQuery));
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Tolto il token dalla barra degli indirizzi e dalla history del browser una
  // volta letto: resta in sessionStorage finché serve.
  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const anteprima = useQuery({
    queryKey: ['invito', token],
    queryFn: () => must(api.POST('/api/v1/invitations/preview', { body: { token } })),
    enabled: !!token,
    // Un token sbagliato o scaduto non migliora riprovando.
    retry: false,
  });

  const accetta = useMutation({
    mutationFn: () => must(api.POST('/api/v1/invitations/accept', { body: { token } })),
    onSuccess: (org) => {
      try {
        sessionStorage.removeItem('invito');
      } catch {
        // niente da pulire se sessionStorage non c'è.
      }
      // La cache di `me` non ha ancora la nuova organizzazione: senza
      // invalidarla, lo shell manderebbe di nuovo a «nessuna organizzazione».
      qc.invalidateQueries({ queryKey: ['me'] });
      navigate({ to: '/orgs/$slug/servers', params: { slug: org.slug } });
    },
  });

  if (!token) {
    return (
      <Cornice>
        <Banner tone="error" title={t('Link incompleto')}>
          {t("Manca il token dell'invito. Chiedi a chi te l'ha mandato di rigenerarlo: vale una volta sola e non è recuperabile.")}
        </Banner>
      </Cornice>
    );
  }

  // Prima si guarda cosa si sta per accettare, poi si entra. L'anteprima non
  // richiede una sessione, quindi si può mostrare a chi arriva dal link.
  if (me.isPending || anteprima.isPending) {
    return (
      <main className="md-login">
        <div className="md-login__card">
          <Logo size={56} anim="costruzione" label={t("Carico l'invito")} />
        </div>
      </main>
    );
  }

  if (anteprima.error) {
    const scaduto = anteprima.error instanceof ApiError && anteprima.error.status === 404;
    return (
      <Cornice>
        <Banner tone="error" title={scaduto ? t('Invito scaduto o già usato') : t('Invito non valido')}>
          {scaduto
            ? t("Gli inviti valgono sette giorni e una volta sola. Chiedine un altro a chi amministra l'organizzazione.")
            : anteprima.error.message}
        </Banner>
      </Cornice>
    );
  }

  const inv = anteprima.data!;

  if (isUnauthenticated(me.error)) {
    return (
      <>
        <Login
          sopra={
            <Banner title={t('Invito a {org}', { org: inv.organization.name })}>
              {t("Entra per accettare: l'invito ti darà il ruolo di {ruolo}.", { ruolo: <strong>{ruolo(inv.role)}</strong> })}
            </Banner>
          }
        />
      </>
    );
  }

  return (
    <Cornice>
      <h1 className="md-headline-small">{inv.organization.name}</h1>
      <p className="md-body-large">
        {t("Sei stato invitato come {ruolo}. Accettando entri nell'organizzazione e vedi i suoi server, le sue istanze e i suoi backup.", {
          ruolo: <strong>{ruolo(inv.role)}</strong>,
        })}
      </p>
      {accetta.error && (
        <Banner tone="error" title={t('Non accettato')}>{accetta.error.message}</Banner>
      )}
      <div className="md-form__actions">
        <Button variant="text" onClick={() => navigate({ to: '/' })}>{t('Non ora')}</Button>
        <Button variant="filled" icon={accetta.isPending ? <Anello /> : undefined} disabled={accetta.isPending} onClick={() => accetta.mutate()}>
          {accetta.isPending ? t('Accetto…') : t('Accetta')}
        </Button>
      </div>
    </Cornice>
  );
}

/** La cornice centrata, la stessa della schermata d'ingresso. */
function Cornice({ children }: { children: React.ReactNode }) {
  return (
    <main className="md-login">
      <div className="md-login__card md-login__card--largo">
        <Brandmark />
        {children}
      </div>
    </main>
  );
}
